import {Box} from '@mui/material';
import {useEffect, useRef, useState} from 'react';
import {ISelectItem} from '../../../shared/types';
import './ArrSelectDropdown.scss';
import ArrTooltip from '../arr-tooltip/ArrTooltip';

type ArrSelectedItemsPanelProps = {
  selectedItems: ISelectItem[];
  itemDisplay: keyof ISelectItem;
  sizeVariant: string;
  disabled: boolean;
  customDisplayLabel?: string;
  dataTestId: string;
};

const ArrSelectedItemsPanel = ({
  selectedItems,
  itemDisplay,
  sizeVariant,
  disabled,
  customDisplayLabel,
  dataTestId,
}: ArrSelectedItemsPanelProps) => {
  const [additionalItemCount, setAdditionalItemCount] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const selectdPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectdPanelRef.current) return;

    if (customDisplayLabel) {
      setDisplayText(customDisplayLabel);
      setAdditionalItemCount(0);
      return;
    }

    const panelWidth = selectdPanelRef.current.offsetWidth;

    // Create measurement span
    const tempSpan = document.createElement('span');
    tempSpan.style.visibility = 'hidden';
    tempSpan.style.whiteSpace = 'nowrap';
    tempSpan.style.position = 'absolute';
    tempSpan.style.font = getComputedStyle(selectdPanelRef.current).font;
    document.body.appendChild(tempSpan);

    // Another span to measure the "+N" width
    const plusSpan = document.createElement('span');
    plusSpan.style.visibility = 'hidden';
    plusSpan.style.whiteSpace = 'nowrap';
    plusSpan.style.position = 'absolute';
    plusSpan.style.font = getComputedStyle(selectdPanelRef.current).font;
    document.body.appendChild(plusSpan);

    let text = '';
    let visibleItemsCount = 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [i, item] of selectedItems.entries()) {
      const itemText = item[itemDisplay]?.toString() ?? '';
      const nextText =
        visibleItemsCount === 0 ? itemText : `${text}, ${itemText}`;

      // Estimate how many items are remaining
      const remaining = selectedItems.length - (visibleItemsCount + 1);
      const countText = remaining > 0 ? `+${remaining}` : '';

      // Measure width of count box if needed
      plusSpan.textContent = countText;
      const countWidth = countText
        ? plusSpan.offsetWidth + 8 /* margin gap */
        : 0;

      tempSpan.textContent = nextText;

      if (tempSpan.offsetWidth + countWidth <= panelWidth) {
        text = nextText;
        visibleItemsCount++;
      } else {
        // Truncate this item to fit
        const prefix = visibleItemsCount === 0 ? '' : `${text},`.trim();
        const original = itemText;
        let end = original.length;
        let truncated = '';

        while (end > 0) {
          truncated = original.substring(0, end) + '...';
          const tentativeText = prefix + truncated;

          tempSpan.textContent = tentativeText;
          const remainingItems = selectedItems.length - (visibleItemsCount + 1);
          const plusText = remainingItems > 0 ? `+${remainingItems}` : '';
          plusSpan.textContent = plusText;
          const updatedCountWidth = plusText ? plusSpan.offsetWidth + 8 : 0;

          if (tempSpan.offsetWidth + updatedCountWidth <= panelWidth) {
            text = tentativeText;
            visibleItemsCount++;
            break;
          }

          end--;
        }

        break;
      }
    }

    const remaining = selectedItems.length - visibleItemsCount;
    setDisplayText(text);
    setAdditionalItemCount(remaining > 0 ? remaining : 0);

    document.body.removeChild(tempSpan);
    document.body.removeChild(plusSpan);
  }, [selectedItems, itemDisplay]);

  const displayTooltip = selectedItems
    .map((item) => item[itemDisplay])
    .filter(Boolean)
    .join(', ');

  const isTruncatedDisplayText =
    displayText.includes('...') || additionalItemCount > 0;

  return (
    <ArrTooltip
      content={customDisplayLabel || displayTooltip}
      showOnTruncate
      tabIndex={-1}
      isTruncated={isTruncatedDisplayText}
    >
      <Box
        className={`arrSelectedItems ${sizeVariant !== 'large' ? 'size-arrSelectItemNames' : ''}`}
      >
        <Box
          ref={selectdPanelRef}
          className={`arrSelectItemNames ${disabled ? 'arrSelectItemNames-disabled' : ''}`}
          data-testid={`${dataTestId}-selected-value-trim`}
        >
          {displayText.trim()}
        </Box>
        {additionalItemCount > 0 && (
          <Box
            className={`arrSelectItemCount ${disabled ? 'arrSelectItemNames-disabled' : ''}`}
            data-testid={`${dataTestId}-selected-more-count`}
          >
            +{additionalItemCount}
          </Box>
        )}
      </Box>
    </ArrTooltip>
  );
};

export default ArrSelectedItemsPanel;
